import React, { useEffect, useState } from 'react';
import { confirmEmail as confirmEmailApi, verifyEmail as verifyEmailApi, confirmVerification as confirmVerificationApi, resendVerificationEmail, getCsrfToken } from '../services/authService';
import { Box, Container, Card, CardContent, Stack, Typography, Button, CircularProgress, TextField, Snackbar, Alert } from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { auth } from '../config/firebase';


export default function EmailVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, idle
  const [message, setMessage] = useState('Verifying your email...');
  const [countdown, setCountdown] = useState(3);
  const [isCrossDevice, setIsCrossDevice] = useState(false);
  const [inputEmail, setInputEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oobCode = params.get('oobCode') || params.get('code') || params.get('oob');

    async function doConfirm(code) {
      // Prefer client-side Firebase verification if auth is configured
      if (auth) {
        try {
          setMessage('Verifying via Firebase...');

          // Attempt to read email from action code before applying
          let userEmail = null;
          try {
            const actionInfo = await checkActionCode(auth, code);
            userEmail = actionInfo?.data?.email || null;
          } catch (ciErr) {
            // ignore - proceed to apply code
          }

          try {
            await applyActionCode(auth, code);
          } catch (applyErr) {
            // Firebase's hosted action page may have already consumed the oobCode
            // before redirecting here (continueUrl redirect). In that case,
            // "auth/invalid-action-code" or "auth/expired-action-code" means
            // the email is likely already verified — check the user's current state.
            const alreadyConsumed = applyErr?.code === 'auth/invalid-action-code' ||
              applyErr?.code === 'auth/expired-action-code' ||
              String(applyErr?.message || '').toLowerCase().includes('expired') ||
              String(applyErr?.message || '').toLowerCase().includes('already') ||
              String(applyErr?.message || '').toLowerCase().includes('invalid');

            if (alreadyConsumed) {
              // Reload user to get fresh emailVerified status
              if (auth.currentUser) {
                try { await auth.currentUser.reload(); } catch {}
              }
              if (auth.currentUser?.emailVerified) {
                // Firebase already verified it — fall through to success handling below
                userEmail = userEmail || auth.currentUser.email;
              } else {
                // Cross-device scenario: user is not signed in here; use stored email
                userEmail = userEmail || localStorage.getItem('verificationEmail');
                if (!userEmail) {
                  setStatus('error');
                  setMessage('Verification link has expired or was already used. Please request a new one.');
                  return;
                }
                // Try backend confirm (Firebase already verified, just sync Firestore)
                try { await confirmVerificationApi(userEmail); } catch {}
                setStatus('success');
                setIsCrossDevice(true);
                setMessage('Your email has been verified. Please return to the app and sign in.');
                try { localStorage.removeItem('verificationEmail'); } catch {}
                return;
              }
            } else {
              throw applyErr; // unexpected error — let outer catch handle it
            }
          }

            // Sync emailVerified to Firestore via backend if we obtained email
              const storedEmail = userEmail || localStorage.getItem('verificationEmail');
              if (storedEmail) {
                try {
                  // Prefer backend confirm-verification which checks Firebase Auth and syncs Firestore
                  const res = await confirmVerificationApi(storedEmail);
                  if (!res || !res.success) {
                    console.warn('Backend confirm-verification returned non-success', res);
                  }
                } catch (syncErr) {
                  // Non-blocking; log and continue
                  console.warn('Failed to confirm verification via backend:', syncErr?.message || syncErr);
                }

                // Firestore sync is handled by `confirmVerificationApi` on the server.
              } else {
                // As a fallback, attempt backend verify by token (older contract)
                try { await verifyEmailApi(code); } catch {}
              }

          // Determine cross-device: if no currentUser, user opened link on different device
          const isLoggedIn = !!auth.currentUser;
          if (isLoggedIn) {
            try { await auth.currentUser.reload(); } catch {}
            setIsCrossDevice(false);
            setMessage('Your email has been verified. Redirecting to login...');
          } else {
            setIsCrossDevice(true);
            setMessage('Your email has been verified. Please return to your original device and sign in.');
          }

          setStatus('success');
          return;
        } catch (err) {
          // If firebase verification fails, fall back to backend confirmation
          console.warn('Firebase applyActionCode failed, falling back to backend confirm:', err?.message || err);
        }
      }

      // Fallback: ask backend to confirm the code (existing flow)
      try {
        const res = await verifyEmailApi(code);
        if (res && res.success) {
          setStatus('success');
          setIsCrossDevice(false);
          setMessage('Your email has been verified. Redirecting to login...');
        } else {
          setStatus('success');
          setIsCrossDevice(false);
          setMessage('Email verified. You may now sign in.');
        }
      } catch (e) {
        setStatus('error');
        setMessage(e?.message || 'Failed to confirm email.');
      }
    }

    if (oobCode) {
      doConfirm(oobCode);
    } else {
      // No oobCode: Firebase already consumed the code and redirected to this page.
      // Per frontend verification guide, call backend confirm-verification with stored email.
      (async () => {
        const storedEmail = new URLSearchParams(location.search).get('email') || localStorage.getItem('verificationEmail');
        if (storedEmail) {
          try { setInputEmail(storedEmail); } catch {}
        }
        if (!storedEmail) {
          // No stored email available — do not prompt the user for input.
          // Instead, treat as completed and redirect to login. The backend
          // should handle the verification when the user clicked the email link.
          setStatus('success');
          setIsCrossDevice(false);
          setMessage('Verification processed. Redirecting to login...');
          try { localStorage.removeItem('verificationEmail'); } catch {}
          return;
        }

        // Ensure CSRF token is available for backend POSTs
        try { await getCsrfToken(); } catch (e) {}

        setMessage('Confirming verification with server...');
        try {
          const res = await confirmVerificationApi(storedEmail);
          if (res && res.success && res.verified) {
            // Synced to Firestore on the server via `confirmVerificationApi`.
            setStatus('success');
            setIsCrossDevice(false);
            setMessage(res.message || 'Your email has been verified. Redirecting to login...');
            try { localStorage.removeItem('verificationEmail'); } catch {}
          } else if (res && res.success && res.verified === false) {
            // Not yet verified in Firebase (rare) — show pending UI with resend option
            setStatus('pending');
            setMessage(res.message || 'Email verification still pending. Please check your inbox.');
          } else {
            // Backend returned failure
            setStatus('error');
            setMessage(res?.message || 'Failed to confirm verification.');
          }
        } catch (err) {
          setStatus('error');
          setMessage(err?.message || 'Failed to confirm verification.');
        }
      })();
    }
  }, [location.search]);

  // Prefill input from localStorage on mount if available
  useEffect(() => {
    try {
      const saved = localStorage.getItem('verificationEmail');
      if (saved && !inputEmail) setInputEmail(saved);
    } catch {}
  }, []);

  async function handleConfirmWithInput(email) {
    if (!email) return;
    setIsSubmitting(true);
    try {
      try { await getCsrfToken(); } catch (e) {}
      const res = await confirmVerificationApi(email);
      if (res && res.success && res.verified) {
        setStatus('success');
        setIsCrossDevice(false);
        setMessage(res.message || 'Your email has been verified. Redirecting to login...');
        try { localStorage.removeItem('verificationEmail'); } catch {}
      } else if (res && res.success && res.verified === false) {
        setStatus('pending');
        setMessage(res.message || 'Email verification still pending. Please check your inbox.');
      } else {
        setStatus('error');
        setMessage(res?.message || 'Failed to confirm verification.');
      }
    } catch (err) {
      setStatus('error');
      setMessage(err?.message || 'Failed to confirm verification.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResend(email) {
    if (!email) return;
    setIsSubmitting(true);
    try {
      try { await getCsrfToken(); } catch (e) {}
      const res = await resendVerificationEmail(email);
      if (res && res.success) {
        setStatus('verifying');
        setMessage('Verification email resent. Please check your inbox.');
        setSnackbar({ open: true, message: 'Verification email resent', severity: 'success' });
      } else {
        setStatus('error');
        setMessage(res?.message || 'Failed to resend verification email.');
        setSnackbar({ open: true, message: res?.message || 'Failed to resend verification email', severity: 'error' });
      }
    } catch (err) {
      setStatus('error');
      setMessage(err?.message || 'Failed to resend verification email.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  useEffect(() => {
    if (status === 'success') {
      const t = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) {
            clearInterval(t);
            navigate('/login');
            return 0;
          }
          return c - 1;
        });
      }, 1000);
      return () => clearInterval(t);
    }
  }, [status, navigate]);

  return (
    <>
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="sm">
          <Card elevation={1} sx={{ borderRadius: 3, maxWidth: 520, mx: 'auto' }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Stack spacing={2.5} alignItems="center">
                <Typography variant="h5" fontWeight={800}>Email verification</Typography>

                {status === 'verifying' && (
                  <Stack spacing={2} alignItems="center">
                    <CircularProgress />
                    <Typography color="text.secondary">{message}</Typography>
                  </Stack>
                )}

                {status === 'success' && !isCrossDevice && (
                  <Stack spacing={2} alignItems="center">
                    <Box sx={{ fontSize: 56, color: 'success.main' }}>✓</Box>
                    <Typography variant="h6">Email Verified Successfully!</Typography>
                    <Typography color="text.secondary" align="center">{message}</Typography>
                    <Button component={RouterLink} to="/login" variant="contained" sx={{ mt: 1 }}>Go to Login</Button>
                    <Typography variant="body2" color="text.secondary">Redirecting automatically in {countdown} seconds...</Typography>
                  </Stack>
                )}

                {status === 'success' && isCrossDevice && (
                  <Stack spacing={2} alignItems="center">
                    <Box sx={{ fontSize: 56, color: 'success.main' }}>✓</Box>
                    <Typography variant="h6">Email Verified Successfully!</Typography>
                    <Typography color="text.secondary" align="center">{message}</Typography>
                    <Button component={RouterLink} to="/login" variant="contained" sx={{ mt: 1 }}>Login on This Device</Button>
                    <Typography variant="body2" color="text.secondary">You can return to your original device and login.</Typography>
                  </Stack>
                )}

                {status === 'error' && (
                  <Stack spacing={2} alignItems="center">
                    <Typography color="error.main">Verification failed</Typography>
                    <Typography color="text.secondary">{message}</Typography>
                    {/* If missing data, allow user to enter email and resend/confirm */}
                    {message && message.toLowerCase().includes('verification data missing') && (
                      <Stack spacing={1} sx={{ width: '100%', mt: 1 }}>
                        <TextField
                          label="Email"
                          value={inputEmail}
                          onChange={(e) => setInputEmail(e.target.value)}
                          fullWidth
                          size="small"
                          placeholder="you@example.com"
                        />
                        <Stack direction="row" spacing={1}>
                          <Button variant="contained" onClick={() => handleConfirmWithInput(inputEmail)} disabled={isSubmitting || !inputEmail}>Confirm</Button>
                          <Button variant="outlined" onClick={() => handleResend(inputEmail)} disabled={isSubmitting || !inputEmail}>Resend</Button>
                        </Stack>
                        <Button component={RouterLink} to="/signup" variant="text" sx={{ mt: 1 }}>Create account</Button>
                      </Stack>
                    )}
                    {!message || !message.toLowerCase().includes('verification data missing') && (
                      <Button component={RouterLink} to="/signup" variant="outlined" sx={{ mt: 1 }}>Create account</Button>
                    )}
                  </Stack>
                )}

                {status === 'collect-email' && (
                  <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
                    <Typography color="text.secondary" align="center">{message}</Typography>
                    <TextField
                      label="Email"
                      value={inputEmail}
                      onChange={(e) => setInputEmail(e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="you@example.com"
                    />
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" onClick={() => handleConfirmWithInput(inputEmail)} disabled={isSubmitting || !inputEmail}>Confirm</Button>
                      <Button variant="outlined" onClick={() => handleResend(inputEmail)} disabled={isSubmitting || !inputEmail}>Resend</Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
